import React, { useState, useEffect, useMemo, useCallback, useReducer } from 'react';
import Header from '../../components/Header';
import { Stack, Button, Typography, Alert } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { connect } from 'react-redux';
import AuthHOC from '../Auth';
import { clearPasswordChangeRequired } from '../../reducers/actions';
import ChangePasswordForm from '../../components/forms/ChangePasswordForm';
import {
    getAll as getAllUsers,
    getById as getUserById,
    setPassword
} from '../../services/userService'
import { changePassword } from '../../services/accountService';

function ChangePasswordView(props) {
    const [saving, setSaving] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loggedInUserName, setLoggedInUserName] = useState(null);
    const [error, setError] = useState('');
    const [warningMsg, setWarningMsg] = useState('');

    const currentUserId  = props.match?.params?.currentUserId;

    // If AdminMode, this means we are changing password for another user.
    // Otherwise we are in self-update mode and are changing our own
    const isAdminMode = Boolean(currentUserId);

    const [formData, setFormData] = useState({
        id: 0,
        username:'',
        userCurrentPassword: '',
        userNewPassword:'',
        userConfirmNewPassword: '',
        email: ''
    });

    function getTitle() {
      const localisation = props.localisation || {};
      return localisation["menu_header_administration_user_details"]
          ? localisation["menu_header_administration_user_details"].element_lang
          : "Administration - Users";
    };

    let title = getTitle();

    useEffect(() => {
        let isCancelled = false;
        let userDetails = [];

        async function loadUserDetails() {
            try {
                setLoading(true);
                setError(null);

                const result = await getUserById(currentUserId);
                if (!result.ok) {
                    setError('Error loading user details. Error status: ' + result.status + " OK: " + result.ok);
                    return;
                }
                if (isCancelled) return;
                userDetails = result.data;

                setFormData({
                    id: userDetails.id || userDetails._id,
                    username: userDetails.username,
                    email: userDetails.email
                });
            }
            catch (err) {
                if (!isCancelled) {
                    setError('Failed to load user details. Error: ' + err.message)
                }
            }
            finally {
                if (!isCancelled) {
                    setLoading(false);
                }
            }
        }

        async function loadLoggedInUserDetails() {
            const username = props.userReducer?.user;
            const userId = props.userReducer?.userId;

            // If we have userId in Redux, try to use it (may still need API call for full details)
            if (username && userId) {
                try {
                    setLoading(true);
                    setError(null);

                    const result = await getUserById(userId);

                    if (isCancelled) return;

                    if (!result.ok) {
                        // Fall back to username lookup if userId lookup fails
                        await loadByUsername(username);
                        return;
                    }

                    const userObject = result.data;
                    setLoggedInUserName(userObject.username);

                    setFormData({
                        id: userObject._id || userObject.id,
                        username: userObject.username,
                        email: userObject.email || ''
                    });
                } catch (err) {
                    if (!isCancelled) {
                        // Fall back to username lookup
                        await loadByUsername(username);
                    }
                } finally {
                    if (!isCancelled) {
                        setLoading(false);
                    }
                }
            } else if (username) {
                // Fallback: Use username lookup if userId not available
                await loadByUsername(username);
            } else {
                setError('Could not determine logged in user');
                setLoading(false);
            }
        }

        async function loadByUsername(username) {
            try {
                setLoading(true);
                setError(null);

                const result = await getAllUsers({username : username});
                if (!result.ok) {
                    setError('Error loading user details. Error status: ' + result.status + " OK: " + result.ok);
                    return;
                }

                const userObject = result.data?.[0];

                if (!userObject) {
                    setError('User not found');
                    return;
                }

                if (isCancelled) return;

                setLoggedInUserName(userObject.username);

                setFormData({
                    id: userObject._id || userObject.id,
                    username: userObject.username,
                    email: userObject.email || ''
                });
            } catch (err) {
                if (!isCancelled) {
                    setError('Failed to load user details. Error: ' + err.message);
                }
            } finally {
                if (!isCancelled) {
                    setLoading(false);
                }
            }
        }

        // we are in admin mode, we are editing a password for a different user
        if (isAdminMode) {
            loadUserDetails();
        }
        // we are in self-update mode. Updating logged in user's password.
        else {
            loadLoggedInUserDetails();
        }       

        return () => {
            isCancelled = true;
        }

    }, [currentUserId, isAdminMode, props.userReducer?.user, props.userReducer?.userId]); // Include Redux user data

    useEffect(() => {
        document.title = title;

        return () => {
            document.title = "Fusion Inscribe";
        }
    }, [title]);

    const handleCancelPasswordChangeClick = () => {
        if (isAdminMode) {
             props.history.push(`/site/administration/UserDetails/${currentUserId}`);
        }
        else {
            // Go back straight to Users page. No need to go back to Edit details page.
            props.history.push(`/site/administration/Users`);
        }
    }

    const handleOnSubmit = async (formData) => {
        setSaving(true);
        setError(null);

        try {
            if (isAdminMode) {
                // Admin resetting password for another user
                const result = await setPassword(formData.id, formData.userNewPassword);
                if (!result.ok) {
                    setError('Failed to update user password. Error status: ' + result.status + " OK: " + result.ok);
                    return;
                }

                // Redirect to Users table with success message
                props.history.push('/site/administration/Users', {
                    successMsg: `Successfully updated ${formData.username}'s password.`
                });
            }
            else {
                // User changing their own password
                const result = await changePassword(formData.userCurrentPassword, formData.userNewPassword, formData.userConfirmNewPassword);
                if (!result.ok) {
                    setError('Failed to change password. Details are incorrect: ' + result.status + " OK: " + result.ok);
                    return;
                }

                props.dispatch(clearPasswordChangeRequired());

                // If this was a forced change, go to dashboard after success.
                // Otherwise go back to user details.
                if (props.userReducer?.requirePasswordChange) {
                    props.history.push('/site/dashboard', {
                        successMsg: 'Your password has been successfully updated. Welcome to Fusion Inscribe.'
                    });
                } else {
                    props.history.push(`/site/administration/UserDetails/${formData.id}`, {
                        successMsg: `${loggedInUserName}: Your password has been successfully changed.`
                    });
                }
            }

        } catch (err) {
            setError(error + ' ' + err.message);
        }
        finally {
            setSaving(false);
        }
    }

    return (
        <div>
            <Header visible={!props.parsed.X3} />
            <div className="page-wrapper">
                <div className='s-page'>
                    <div className="s-page-header">
                        <Stack 
                            spacing={0.5} 
                            alignItems="flex-start"
                            sx={{ mb: 3, textAlign: "left" }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {title}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                               {isAdminMode ? 'Change password for a user' : 'Reset your password'}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                User: {formData.username}
                            </Typography>
                        </Stack>
                    </div>
                <div className="s-page-actions">
                    <Button
                        type="button"
                        variant='outlined'
                        onClick={handleCancelPasswordChangeClick}
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            textTransform: 'none',
                            color: 'text.secondary',
                            borderColor: 'divider',
                            '&:hover': {
                                color: 'primary.main',
                                borderColor: 'primary.main'
                            }
                        }}
                        >
                        {currentUserId ? "Back to User Details" : "Back to Users"}
                    </Button>
                </div>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {warningMsg && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {warningMsg}
                    </Alert>
                )}
                    <div className="s-content-panel">
                    <ChangePasswordForm
                        initialValues={formData}
                        onCancel={handleCancelPasswordChangeClick}
                        onSubmit={handleOnSubmit}
                        isAdminMode={isAdminMode}
                        loading={loading}
                    />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default connect(function (state) { return state; })(
  AuthHOC(ChangePasswordView, { name: 'administration' })
);